import cn from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import openPopupOperation from 'editor/src/store/app/module/popup/operation/openPopupOperation';
import getSettingsValue from 'editor/src/store/editor/selector/getSettingsValue';
import { SettingsProperty } from 'editor/src/store/editor/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import setSelectedVariationGroupOperation from 'editor/src/store/variants/operation/setSelectedVariationGroupOperation';
import toggleLinkVariantGroupOperation from 'editor/src/store/variants/operation/toggleLinkVariantGroupOperation';
import getVariationGroupsFromDesignOptions from 'editor/src/store/variants/selector/getVariationGroupsFromDesignOptions';
import { linkAllVariantsAction, toggleMultiOptionAction, updateVariationGroupsAction } from 'editor/src/store/variants/slice';
import { VariationGroup } from 'editor/src/store/variants/types';

import { RootState } from 'editor/src/store';

import ContentBlock from 'editor/src/component/DesktopSidebar/TabContents/Elements/ContentBlock';
import { PopupName } from 'editor/src/component/Popup/popups';
import { useCanvasRendering } from 'editor/src/component/SpreadPreview';

import DesignOptionsVariationGroupList from './DesignOptionsVariationGroupList';
import getPartitionLabel from './getPartitionLabel';
import getSpreadCountForGroup from './getSpreadCountForGroup';
import ProductSwitcher from './ProductSwitcher';
import ProductVariantEntry, { Ref as EntryRef } from './ProductVariantEntry';
import VariantLock from './VariantLock';

import styles from './index.module.scss';

function selector(state: RootState) {
  const { variationGroups, selectedGroupKey, selectedMultiOptions, product, configuration, designTemplates } =
    state.variants;
  return {
    variationGroups,
    selectedGroupKey,
    options: selectedMultiOptions,
    product,
    configuration,
    designTemplates,
    unit: getSettingsValue(state, SettingsProperty.units),
    spreadIndex: state.editor.currentSpreadIndex,
  };
}

interface PartitionGroup {
  group: VariationGroup;
  globalIndex: number;
}

interface SpreadCountPartition {
  spreadCount: number | undefined;
  groups: PartitionGroup[];
  allLinked: boolean;
  label: string;
}

function ProductVariantList() {
  const { variationGroups, selectedGroupKey, options, product, configuration, designTemplates, unit, spreadIndex } =
    useSelector(selector, shallowEqual);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const allLinked = useMemo(
    () => variationGroups.reduce((linked, group) => linked && group.linked, true),
    [variationGroups],
  );

  const spreadCountPartitions = useMemo((): SpreadCountPartition[] => {
    const partitionMap = new Map<number | undefined, PartitionGroup[]>();
    variationGroups.forEach((group, globalIndex) => {
      const count = getSpreadCountForGroup(group, designTemplates);
      const existing = partitionMap.get(count) || [];
      existing.push({ group, globalIndex });
      partitionMap.set(count, existing);
    });
    const entries = Array.from(partitionMap.entries());
    return entries.map(([spreadCount, groups]) => {
      const rawGroups = groups.map((pg) => pg.group);
      const otherPartitions = entries.filter(([sc]) => sc !== spreadCount).map(([, g]) => g.map((pg) => pg.group));
      return {
        spreadCount,
        groups,
        allLinked: rawGroups.reduce((linked, group) => linked && group.linked, true),
        label: getPartitionLabel(rawGroups, otherPartitions, product),
      };
    });
  }, [variationGroups, designTemplates, product]);

  // Only show grouped view when ALL partitions have a known spread count.
  // When templates are still loading, some groups return spreadCount=undefined
  // which creates a spurious extra partition. Without this guard, the UI would
  // briefly flash the grouped layout then collapse back to a flat list once
  // templates arrive — a visible flicker for products like posters where all
  // variants ultimately have the same spread count.
  const hasMultipleSpreadGroups =
    spreadCountPartitions.length > 1 && spreadCountPartitions.every((p) => p.spreadCount !== undefined);
  const { requestRender } = useCanvasRendering();
  const groupsFromDesignOptions = useSelector(getVariationGroupsFromDesignOptions);
  const selectedDesignOptionValue = useSelector((state) => state.variants.selectedDesignOptionValue);
  const unavailableProducts = useSelector((state) => state.variants.product.unavailableProducts);
  const selectedMultiOptions = useSelector((state) => state.variants.selectedMultiOptions);

  const linkAllVariants = useCallback((groupKeys?: string[]) => {
    dispatch(
      openPopupOperation(PopupName.LOCAL_CONFIRMATION_POPUP, {
        popupTitle: t('editor-link-all-variants-confirmation-title'), // txt_link_all_variants_confirmation_title
        textLines: [t('editor-link-all-variants-confirmation-text')], // txt_link_all_variants_confirmation_text
        options: [
          { title: t('editor-cancel') }, // txt_cancel
          {
            title: t('editor-link-all-variants-confirmation-yes-option'),
            onConfirm: () => dispatch(linkAllVariantsAction(groupKeys)), // txt_link_all_variants_confirmation_yes_option
          },
        ],
      }),
    );
  }, []);

  const onLinkToggle = useCallback((group: VariationGroup) => {
    if (group.linked) {
      dispatch(toggleLinkVariantGroupOperation(group));
      return;
    }

    dispatch(
      openPopupOperation(PopupName.LOCAL_CONFIRMATION_POPUP, {
        popupTitle: t('editor-link-variant-confirmation-title', {
          formatName: group.title,
        }),
        textLines: [t('editor-link-variant-confirmation-text')],
        options: [
          { title: t('editor-cancel') },
          {
            title: t('editor-link-all-variants-confirmation-yes-option'),
            onConfirm: () => {
              dispatch(toggleLinkVariantGroupOperation(group));
            },
          },
        ],
      }),
    );
  }, []);

  const onSelect = useCallback((groupKey: string) => {
    dispatch(setSelectedVariationGroupOperation(groupKey));
  }, []);

  const refs = useMemo(() => variationGroups.map(() => React.createRef<EntryRef>()), [variationGroups.length]);
  const [partitionHeights, setPartitionHeights] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (hasMultipleSpreadGroups) {
        const newHeights: { [key: string]: number } = {};
        spreadCountPartitions.forEach((partition) => {
          let y = 0;
          partition.groups.forEach((pg) => {
            const ref = refs[pg.globalIndex];
            ref?.current?.setY(y);
            y += ref?.current?.getHeight() || 0;
          });
          newHeights[String(partition.spreadCount)] = y;
        });
        setPartitionHeights(newHeights);
      } else {
        let y = 0;
        refs.forEach((ref) => {
          ref.current?.setY(y);
          y += ref.current?.getHeight() || 0;
        });
      }
    }, 0);

    return () => window.clearTimeout(handle);
  }, [variationGroups, hasMultipleSpreadGroups, spreadCountPartitions]);

  const { disableLinkFeature } = configuration;

  const onDeleteVariant = (variantKey: string) => {
    const variantKeyWithoutGroupIndex = variantKey.replace(/-\d$/, '');
    // filter the variant from all groups
    const newVariationGroups = variationGroups.filter(
      ({ key }) => key.replace(/-\d$/, '') !== variantKeyWithoutGroupIndex,
    );

    // filter the selected options base on variants list
    const multiOptions = cloneDeep(selectedMultiOptions);
    Object.keys(selectedMultiOptions).forEach((optionKey) => {
      multiOptions[optionKey] = selectedMultiOptions[optionKey].filter(({ value, dependedOptions }) => {
        return newVariationGroups.some(({ variationsInfo }) => {
          return variationsInfo.some(({ variation }) => {
            return (
              variation[optionKey] === value && dependedOptions.every(({ key, value }) => variation[key] === value)
            );
          });
        });
      });
    });

    dispatch(updateVariationGroupsAction(newVariationGroups));
    dispatch(toggleMultiOptionAction({ multiOptions }));

    // set to focus other variant if the selected one got deleted
    if (selectedGroupKey?.replace(/-\d$/, '') === variantKeyWithoutGroupIndex && newVariationGroups[0]) {
      dispatch(setSelectedVariationGroupOperation(newVariationGroups[0].key));
    }
  };

  return (
    <div
      className={cn(styles.ProductVariantList, 'cy-variant-list', 'intro-tour-variants-list', {
        [styles.linkDisabled]: disableLinkFeature,
      })}
    >
      <ProductSwitcher />
      <ContentBlock fill scroll noBorder={disableLinkFeature}>
        {groupsFromDesignOptions?.length && selectedDesignOptionValue ? (
          <DesignOptionsVariationGroupList
            selectedDesignOptionValue={selectedDesignOptionValue}
            groups={groupsFromDesignOptions}
            onDelete={groupsFromDesignOptions.length > 0 ? onDeleteVariant : undefined}
          />
        ) : (
          <>
            {!disableLinkFeature && !hasMultipleSpreadGroups && (
              <div className={cn(styles.header, 'cy-link-all')}>
                {t('editor-link-all-variants') /* txt_link_all_variants_button */}
                <VariantLock isLinked={allLinked} disabled={allLinked} onToggle={linkAllVariants} />
              </div>
            )}
            {!disableLinkFeature && hasMultipleSpreadGroups && (
              <div className={styles.spreadGroupNotice}>
                {t(
                  'editor-variants-grouped-by-sides',
                  'Variants are grouped by number of print sides. Design changes only sync within the same group.',
                )}
              </div>
            )}
            <div className={styles.separator} />
            {hasMultipleSpreadGroups ? (
              spreadCountPartitions.map((partition) => {
                const partitionGroupKeys = partition.groups.map((pg) => pg.group.key);
                return (
                  <div key={partition.spreadCount ?? 'unknown'} className={styles.spreadGroup}>
                    <div className={cn(styles.spreadGroupHeader, 'cy-spread-group-header')}>
                      <span className={styles.spreadGroupLabel}>{partition.label}</span>
                      {!disableLinkFeature && partition.groups.length > 1 && (
                        <VariantLock
                          isLinked={partition.allLinked}
                          disabled={partition.allLinked}
                          onToggle={() => linkAllVariants(partitionGroupKeys)}
                        />
                      )}
                    </div>
                    <div
                      className={styles.list}
                      style={{ height: partitionHeights[String(partition.spreadCount)] || 'auto' }}
                    >
                      {partition.groups.map((pg) => (
                        <ProductVariantEntry
                          key={`${pg.group.key}-${pg.group.designOptions?.join('-')}`}
                          group={pg.group}
                          isSelected={pg.group.key === selectedGroupKey}
                          options={options}
                          linkEnabled={!disableLinkFeature}
                          onSelect={onSelect}
                          onLinkToggle={onLinkToggle}
                          requestRender={requestRender}
                          product={product}
                          unit={unit}
                          ref={refs[pg.globalIndex]}
                          spreadIndex={spreadIndex}
                          onDelete={variationGroups.length > 1 ? onDeleteVariant : undefined}
                          unavailableProducts={unavailableProducts}
                        />
                      ))}
                    </div>
                    <div className={styles.separator} />
                  </div>
                );
              })
            ) : (
              <div className={styles.list}>
                {variationGroups.map((group, i) => (
                  <ProductVariantEntry
                    key={`${group.key}-${group.designOptions?.join('-')}`}
                    group={group}
                    isSelected={group.key === selectedGroupKey}
                    options={options}
                    linkEnabled={!disableLinkFeature}
                    onSelect={onSelect}
                    onLinkToggle={onLinkToggle}
                    requestRender={requestRender}
                    product={product}
                    unit={unit}
                    ref={refs[i]}
                    spreadIndex={spreadIndex}
                    onDelete={variationGroups.length > 1 ? onDeleteVariant : undefined}
                    unavailableProducts={unavailableProducts}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </ContentBlock>
    </div>
  );
}

export default React.memo(ProductVariantList);
